/**
 * One picture per sample set, shared by the sample picker and the sample job it
 * creates, so the card someone chose and the job that opens look the same
 * (onboarding redo, 2026-09-27).
 */

import type { ComponentType } from 'react';
import {
  Car,
  FileText,
  GraduationCap,
  House,
  Receipt,
  Scale,
  ShieldCheck,
  Stethoscope,
  Truck,
  Umbrella,
  Users,
  Zap,
  type LucideProps,
} from 'lucide-react';

const SET_ICONS: Record<string, ComponentType<LucideProps>> = {
  accounts_payable: Receipt,
  auto_dealership: Car,
  freight_logistics: Truck,
  healthcare_rcm: Stethoscope,
  hr: Users,
  insurance: ShieldCheck,
  legal: Scale,
  mortgage: House,
  pc_claims: Umbrella,
  student_records: GraduationCap,
  utility_billing: Zap,
};

export const sampleSetIcon = (corpus: string): ComponentType<LucideProps> =>
  SET_ICONS[corpus.replace(/_(small|large)_set$/, '')] ?? FileText;

// Which sample set each sample job came from. Cosmetic, like the job pictures
// themselves (JobIdentity keeps those in the same browser storage).
const key = 'cpv6-sample-job-sets-v1';
let cached: Record<string, string> | undefined;
const read = (): Record<string, string> => {
  if (cached) return cached;
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? 'null') as unknown;
    if (value && typeof value === 'object' && !Array.isArray(value))
      return (cached = value as Record<string, string>);
  } catch {
    /* Storage unavailable: the job keeps its ordinary picture. */
  }
  return (cached = {});
};

export function rememberSampleJobSet(jobId: string, corpus: string): void {
  const sets = read();
  sets[jobId] = corpus;
  try {
    localStorage.setItem(key, JSON.stringify(sets));
  } catch {
    /* This session still shows it. */
  }
}

export const sampleSetOfJob = (jobId: string): string | null => read()[jobId] ?? null;
